import { memo, useMemo } from 'react';

import { cn } from '@veraclins-dev/utils';

import { type ListVariants, listVariants } from './utils/variants/list';
import { extractStyleProps } from './utils/variants/styles';

type ListElement = 'ul' | 'ol';

interface ListProps
  extends
    Omit<React.HTMLAttributes<HTMLElement>, keyof ListVariants>,
    ListVariants {
  /** The HTML element to render the List as. Defaults based on variant if not specified. */
  component?: ListElement;
  /** ARIA role for accessibility. Useful for 'listbox' in autocomplete. */
  role?: string;
  /** Whether this list is used for selection (adds selection-related ARIA attributes) */
  selectable?: boolean;
  /** The number an ordered list starts counting from. */
  start?: number;
}

/**
 * A flexible list component that supports both ordered and unordered lists with customizable styling.
 * This enables consistent list styling across the application with a consistent API.
 */
function BaseList({
  component,
  className,
  role,
  selectable = false,
  variant = 'none',
  markerPosition = 'inside',
  children,
  ...props
}: ListProps) {
  const { styleProps, others } = extractStyleProps(props);

  // When the caller doesn't pin a tag, derive it from variant:
  // ordered-* → <ol>, everything else → <ul>. This is the documented
  // contract; defaulting `component` to 'ul' here would shadow the
  // variant check below and make ordered lists render with <ul>.
  const Component = useMemo(() => {
    if (component) return component;
    if (variant?.startsWith('ordered-')) return 'ol';
    return 'ul';
  }, [component, variant]);

  const marker = useMemo(() => {
    if (variant === 'none') return 'none';
    if (variant?.startsWith('unordered-')) {
      return variant.replace('unordered-', '');
    }
    if (variant?.startsWith('ordered-')) {
      return variant.replace('ordered-', '');
    }
    return 'default';
  }, [variant]);

  return (
    <Component
      data-slot="list"
      role={role}
      aria-label={selectable ? 'Options' : undefined}
      data-marker={marker}
      className={cn(
        listVariants({
          variant,
          markerPosition,
          ...styleProps,
          className,
        }),
      )}
      {...others}
    >
      {children}
    </Component>
  );
}

/**
 * A memoized flexible list component that supports both ordered and unordered lists with customizable styling.
 * This enables consistent list styling across the application with a consistent API.
 *
 * `markerPosition` defaults to `inside`: markers sit in the text box, so a long item
 * wraps under its bullet or number. With `outside`, markers sit in the start padding
 * and wrapped lines align with the first word. The list then gets a start gutter sized
 * to the marker: `2em` for bullets, `2.5em` for decimal and alpha, `3.5em` for roman.
 * These fit three-digit numbers and roman numerals up to xxxviii. A list with
 * `variant="none"` gets no gutter. For longer markers, set the `ps` style prop to
 * widen the gutter.
 * @param {ListProps} props - The props for the List component.
 * @returns {JSX.Element} A rendered HTML element with applied styles and children.
 * @example
 * ```tsx
 * <List variant="unordered-disc" pl={6} my={4}>
 *   <ListItem variant="interactive" size="md">List item 1</ListItem>
 *   <ListItem variant="selected" color="primary">List item 2</ListItem>
 * </List>
 * ```
 * @example
 * ```tsx
 * // Long items wrap under the text, not under the bullet.
 * <List variant="ordered-decimal" markerPosition="outside">
 *   <ListItem>A long step that wraps onto a second line</ListItem>
 * </List>
 * ```
 * @example
 * ```tsx
 * // Roman numerals past xxxviii need a wider gutter than the default.
 * <List variant="ordered-upper-roman" markerPosition="outside" start={386} ps={24}>
 *   <ListItem>A long step that wraps onto a second line</ListItem>
 * </List>
 * ```
 * @category data-content
 */
const List = memo(BaseList);

export { List, type ListProps };
export default BaseList;
